// Pure helpers for images that arrive as CDN links. Deliberately free of any network or
// storage import so the CSV parser can use it — see imageImport.js for the fetching side.

const MIME_EXT = {
  'image/jpeg': 'jpg',
  'image/jpg':  'jpg',
  'image/png':  'png',
  'image/webp': 'webp',
  'image/gif':  'gif',
  'image/tiff': 'tif',
  'image/avif': 'avif',
  'image/heic': 'heic',
};

/**
 * Derives a filename from a URL. Query strings are dropped (Shopify's `?v=` cache-buster
 * would otherwise end up in the name), but the stem is kept intact because the collection
 * importer matches images to products on filename.
 */
export function filenameFromUrl(url) {
  try {
    const { pathname } = new URL(url);
    const last = pathname.split('/').filter(Boolean).pop() || '';
    const decoded = decodeURIComponent(last);
    const cleaned = decoded.replace(/[^a-zA-Z0-9._-]/g, '_');
    if (cleaned && cleaned !== '.') return cleaned;
  } catch { /* fall through to the generic name below */ }
  return 'image';
}

// Shopify and most image CDNs serve extensionless or transformed URLs, so trust the
// response's content type over whatever the path happened to end in.
export function withExtension(name, mimeType) {
  const ext = MIME_EXT[mimeType] || 'jpg';
  if (new RegExp(`\\.${ext}$`, 'i').test(name)) return name;
  const stem = name.replace(/\.[a-zA-Z0-9]{1,5}$/, '');
  return `${stem || 'image'}.${ext}`;
}

const PUBLIC_PREFIX = '/storage/v1/object/public/';

/**
 * True when a link already points at this workspace's own storage. Such a link needs no
 * re-hosting — without this check a sheet containing URLs we produced on a previous import
 * would be downloaded through the proxy and stored a second time under a new path.
 */
export function isOwnStorageUrl(url) {
  const base = process.env.REACT_APP_SUPABASE_URL;
  return !!base && typeof url === 'string' && url.startsWith(`${base}${PUBLIC_PREFIX}`);
}

/**
 * The storage path a given source link is re-hosted to. Derived from the link itself rather
 * than from the product id and a timestamp, so the same source always resolves to the same
 * object: re-running an import reuses what's already there instead of downloading and
 * storing a second copy, and a run whose products never got saved can simply be run again.
 *
 * Scoped per tenant so two workspaces importing the same CDN URL don't share an object —
 * one deleting it would otherwise break the other.
 */
export async function importedPathFor(scope, sourceUrl, filename) {
  const bytes = new TextEncoder().encode(sourceUrl);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  const hash = Array.from(new Uint8Array(digest).slice(0, 8))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
  return `imported/${scope || 'shared'}/${hash}-${filename}`;
}

/**
 * A URL that downloads rather than displays. Supabase honours `?download=<name>` on a public
 * object by setting Content-Disposition, which is what makes this work at all: the `download`
 * attribute on an <a> is ignored for cross-origin hrefs, and storage is a different origin
 * from the app. Returns null for a link that isn't ours, where we can't set that header.
 */
export function downloadUrlFor(url, filename) {
  if (!isOwnStorageUrl(url)) return null;
  try {
    const u = new URL(url);
    u.searchParams.set('download', filename || filenameFromUrl(url));
    return u.toString();
  } catch {
    return null;
  }
}
